package horcrux.frontend.common.clients

import arrow.core.Either
import arrow.core.raise.either
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.FirebaseOptions
import dev.gitlive.firebase.auth.EmailAuthProvider
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.auth.externals.User
import dev.gitlive.firebase.initialize
import horcrux.common.HorcruxEither
import horcrux.common.api.dtos.types.EmailAddress
import horcrux.common.utils.runIOAndCatch
import horcrux.common.utils.toEither
import horcrux.frontend.common.HorcruxClientError
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.sync.Mutex

value class BackendAPIAccessToken(val value: String)

class AuthenticationClient {
    companion object {
        private var userMaybe: User? = null
        private val userLock: Mutex = Mutex(locked = true)
        private const val MAX_TICKS: Long = 50L
        private const val TICK_DURATION_IN_MILLIS: Long = 50L
        private val FIREBASE_OPTIONS: FirebaseOptions = FirebaseOptions(
            applicationId = "1:118170158486:web:8b836d03b6ee3a44ed49b7",
            apiKey = "AIzaSyBSGw6xFz2E8LSl5njtH2TwWbv2scThRVs",
            storageBucket = "horcrux-27313.appspot.com",
            projectId = "horcrux-27313",
            authDomain = "horcrux-27313.firebaseapp.com"
        )

        init {
            Firebase.initialize(options = FIREBASE_OPTIONS)

            Firebase.auth.js.onAuthStateChanged { user ->
                userMaybe = user
                if (userLock.isLocked) {
                    userLock.unlock()
                }
            }
        }
    }

    class UnauthenticatedException : HorcruxClientError()
    class UnverifiedEmailException : HorcruxClientError() {
        companion object {
            const val BANNER_MESSAGE: String = "You must verify your email before signing in. Please check your inbox."
        }
    }

    suspend fun signUp(email: EmailAddress, password: String): HorcruxEither<Unit> =
        runIOAndCatch {
            val authResult = Firebase.auth.createUserWithEmailAndPassword(email.value, password)
            authResult.user?.sendEmailVerification()
            authResult
        }

    private fun User.checkIfEmailVerified(): HorcruxEither<Unit> = either {
        if (!this@checkIfEmailVerified.emailVerified) raise(UnverifiedEmailException())
    }

    suspend fun signIn(
        email: EmailAddress,
        password: String,
    ): Either<Throwable, Unit> = either {
        val user = runIOAndCatch {
            val emailAuthProvider = EmailAuthProvider.credential(email.value, password)
            Firebase.auth.signInWithCredential(emailAuthProvider).js.user ?: error("User not found")
        }.bind()

        user.checkIfEmailVerified().bind()
    }

    private suspend fun <A> withTimeout(block: () -> A?): HorcruxEither<A> {
        var resultMaybe: A? = null
        var numTicks: Long = 0
        while (resultMaybe == null && numTicks < MAX_TICKS) {
            resultMaybe = block()
            delay(TICK_DURATION_IN_MILLIS)
            numTicks += 1
        }
        return resultMaybe.toEither()
    }

    private suspend fun <A> runBlockWithTimeoutAndCatch(block: () -> A?): HorcruxEither<A> =
        withTimeout { block() }
            .mapLeft {
                UnauthenticatedException()
            }

    suspend fun getUser(): HorcruxEither<User> {
        return runBlockWithTimeoutAndCatch {
            if (userLock.isLocked) {
                null
            } else {
                userMaybe
            }
        }
    }

    suspend fun signOut(): HorcruxEither<Unit> = either {
        runIOAndCatch {
            Firebase.auth.js.signOut().await()
            userMaybe = null
            userLock.tryLock()
        }.bind()
    }

    suspend fun getBackendAPIAccessToken(): HorcruxEither<BackendAPIAccessToken> {
        return either {
            getUser()
                .map { user ->
                    user.checkIfEmailVerified().bind()
                    val idToken = runIOAndCatch {
                        user
                            .getIdToken(false)
                            .await()
                    }.bind()
                    BackendAPIAccessToken(idToken)
                }
                .bind()
        }
    }

    suspend fun isLoggedIn(): Boolean = getBackendAPIAccessToken().fold({ false }) { true }

    suspend fun sendPasswordResetEmail(emailAddress: EmailAddress): HorcruxEither<Unit> = either {
        runIOAndCatch {
            Firebase.auth.sendPasswordResetEmail(emailAddress.value)
        }.bind()
    }
}
